import { type ReactNode, useCallback } from 'react';
import * as R from 'remeda';
import type { Page } from '@rossum/api-client/pages';
import { type CameraSlice, createStoreHooks } from '../../../canvas/core';
import { useTransformWrappers } from '../feature-extension/DocumentCanvasContext';
import type { PageLayoutData } from '../layout/types';
import type { PageLayoutSlice } from '../store/types';
import { usePageContent } from './usePageContent';

type PageRenderDataDeps = CameraSlice & PageLayoutSlice;

const { useStore } = createStoreHooks<PageRenderDataDeps>();

export const usePageRenderData = (pageModel: Page, layout: PageLayoutData) => {
  const pageState = useStore(state =>
    state.pageLayout.selectors.getPageState(layout.pageId)
  );

  const {
    data: contentBlob,
    isSuccess,
    error,
  } = usePageContent(pageModel.content, pageModel.id, {
    enabled: pageState.visible,
  });

  const transformWrappers = useTransformWrappers();

  const wrapWithTransforms = useCallback(
    (content: ReactNode): ReactNode =>
      R.pipe(
        transformWrappers,
        R.reverse(),
        R.reduce(
          (acc: ReactNode, Wrapper) => (
            <Wrapper pageId={pageModel.id}>{acc}</Wrapper>
          ),
          content
        )
      ),
    [transformWrappers, pageModel.id]
  );

  return {
    pageState,
    contentBlob: isSuccess ? contentBlob : undefined,
    isLoading: !isSuccess && !error,
    error: error instanceof Error ? error : null,
    wrapWithTransforms: wrapWithTransforms as (content: ReactNode) => ReactNode,
  };
};
